// useNotifications.js
// ─────────────────────────────────────────────────────────────────────────────
// Real-time notifications via Server-Sent Events (SSE).
// Falls back to 30-second polling if SSE is unavailable or repeatedly fails.
//
// Usage (unchanged API):
//   const { notifications, dismissOne, dismissAll, refresh } = useNotifications();
//
// SSE flow:
//   1. Opens EventSource at /sse/notifications?token=<JWT>
//   2. Server sends 'init' event (full state) immediately on connect
//   3. Server sends 'notifications' events (delta, new IDs only)
//   4. Server sends 'reconnect' after ~10 min → EventSource auto-reconnects
//      with the latest token from localStorage (silently refreshed by AuthContext)
//   5. On 3 consecutive EventSource errors → silently falls back to polling
//   6. On tab visibility change → reconnects SSE for fresh delta
//   7. On hejvi:token-refreshed → reconnects SSE sofort mit neuem Token

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// ── Config ────────────────────────────────────────────────────────────────────

/** Max consecutive SSE errors before falling back to polling */
const SSE_MAX_ERRORS = 3;

/** Fallback polling interval (ms) — only used when SSE is unavailable */
const POLL_FALLBACK_MS = 30_000;

// ── Hook ──────────────────────────────────────────────────────────────────────

export default function useNotifications() {
  const { makeAuthenticatedRequest } = useAuth();

  const [notifications, setNotifications] = useState([]);

  // SSE state refs — stable across renders, no re-render on change
  const sseRef          = useRef(null);   // current EventSource instance
  const errorCount      = useRef(0);      // consecutive SSE errors
  const usingSSE        = useRef(false);  // true while SSE is active
  const pollTimerRef    = useRef(null);   // fallback polling timer
  const reconnectTimer  = useRef(null);   // backoff timer für SSE-Reconnect
  const reconnectDelay  = useRef(1000);   // aktueller Backoff in ms (1s→2s→4s→8s→max 30s)

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Merge incoming notifications into state, newest first, deduped, max 20 */
  const mergeNotifications = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setNotifications((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const fresh = incoming.filter((n) => !existingIds.has(n.id));
      if (fresh.length === 0) return prev;
      return [...fresh, ...prev].slice(0, 20);
    });
  }, []);

  /** Full replace (used on 'init' event) */
  const replaceNotifications = useCallback((data) => {
    if (Array.isArray(data)) setNotifications(data);
  }, []);

  // ── Fallback: HTTP fetch ──────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    try {
      const res  = await makeAuthenticatedRequest('/notifications');
      const data = (res && typeof res.json === 'function') ? await res.json() : res;
      if (data?.status === 'ok' && Array.isArray(data.data)) {
        replaceNotifications(data.data);
      }
    } catch (err) {
      console.debug('[useNotifications] poll fetch error', err);
    }
  }, [makeAuthenticatedRequest, replaceNotifications]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    fetchNotifications();
    pollTimerRef.current = setInterval(fetchNotifications, POLL_FALLBACK_MS);
  }, [fetchNotifications]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ── SSE connection ────────────────────────────────────────────────────────

  const closeSse = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    usingSSE.current = false;
  }, []);

  const openSse = useCallback(async () => {
    if (typeof EventSource === 'undefined') {
      console.info('[useNotifications] EventSource not available — using polling');
      startPolling();
      return;
    }

    const token = localStorage.getItem('hejvi_access_token');
    if (!token) {
      console.debug('[useNotifications] no access token — using polling');
      startPolling();
      return;
    }

    closeSse();

    // Resolve the API base URL
    let base = '';
    try {
      const { appConfig } = await import('../config');
      base = appConfig?.apiBaseUrl ?? '';
    } catch { /* use empty base */ }

    const url = `${base}/sse/notifications?token=${encodeURIComponent(token)}`;
    const sse = new EventSource(url);
    sseRef.current   = sse;
    usingSSE.current = true;
    stopPolling();

    // ── 'init' — full current notification list ───────────────────────────
    sse.addEventListener('init', (e) => {
      errorCount.current     = 0;
      reconnectDelay.current = 1000; // Backoff nach erfolgreicher Verbindung zurücksetzen
      try {
        const payload = JSON.parse(e.data);
        if (payload?.status === 'ok') replaceNotifications(payload.data);
      } catch { /* ignore malformed */ }
    });

    // ── 'notifications' — delta push (new IDs only) ───────────────────────
    sse.addEventListener('notifications', (e) => {
      errorCount.current = 0;
      try {
        const payload = JSON.parse(e.data);
        if (payload?.status === 'ok') mergeNotifications(payload.data);
      } catch { /* ignore malformed */ }
    });

    // ── 'reconnect' — server gracefully closing after max lifetime ────────
    sse.addEventListener('reconnect', () => {
      closeSse();
      setTimeout(openSse, 500);
    });

    // ── error — sofort schließen und mit frischem Token reconnecten ───────
    // Nach Suspend/Resume enthält die bestehende URL einen abgelaufenen Token.
    // Wir schließen die Verbindung und öffnen sie neu — openSse() liest den
    // aktuellen Token immer frisch aus localStorage.
    // Exponentieller Backoff (1s→2s→4s→8s→30s max) verhindert Tight-Loops.
    // Nach SSE_MAX_ERRORS aufeinanderfolgenden Fehlern: Polling-Fallback.
    sse.onerror = () => {
      errorCount.current += 1;
      console.debug(`[useNotifications] SSE error #${errorCount.current}, backoff=${reconnectDelay.current}ms`);

      closeSse(); // alten EventSource (mit altem Token) sofort wegwerfen

      if (errorCount.current >= SSE_MAX_ERRORS) {
        console.warn('[useNotifications] SSE repeatedly failed — falling back to polling');
        reconnectDelay.current = 1000; // Reset für spätere Wiederversuche
        startPolling();
        return;
      }

      // Mit aktuellem Token (ggf. gerade von AuthContext refreshed) neu verbinden
      const delay = reconnectDelay.current;
      reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30_000);
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        openSse();
      }, delay);
    };
  }, [closeSse, mergeNotifications, replaceNotifications, startPolling, stopPolling]);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  useEffect(() => {
    openSse();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (usingSSE.current) {
          closeSse();
          openSse();
        } else if (!pollTimerRef.current) {
          startPolling();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Nach erfolgreichem Token-Refresh SSE mit neuem Token reconnecten.
    // AuthContext.doRefresh() feuert dieses Event nach jedem erfolgreichen Refresh —
    // damit bekommt die SSE-Verbindung sofort neue Credentials statt auf den
    // nächsten onerror zu warten (der erst nach dem SSE-Timeout kommt).
    const handleTokenRefreshed = () => {
      if (usingSSE.current || sseRef.current) {
        closeSse();
        openSse();
      }
    };
    window.addEventListener('hejvi:token-refreshed', handleTokenRefreshed);

    return () => {
      closeSse();
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('hejvi:token-refreshed', handleTokenRefreshed);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dismiss one ───────────────────────────────────────────────────────────

  const dismissOne = useCallback(
    async (notificationId) => {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      try {
        const res = await makeAuthenticatedRequest('/notifications/dismiss', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ notification_id: notificationId }),
        });
        if (res && typeof res.json === 'function') await res.json();
      } catch (err) {
        console.debug('[useNotifications] dismiss error', err);
        fetchNotifications();
      }
    },
    [makeAuthenticatedRequest, fetchNotifications]
  );

  // ── Dismiss all ───────────────────────────────────────────────────────────

  const dismissAll = useCallback(
    async (ids) => {
      const idsToSend = ids ?? notifications.map((n) => n.id);
      setNotifications([]);
      try {
        const res = await makeAuthenticatedRequest('/notifications/dismiss-all', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ notification_ids: idsToSend }),
        });
        if (res && typeof res.json === 'function') await res.json();
      } catch (err) {
        console.debug('[useNotifications] dismiss-all error', err);
        fetchNotifications();
      }
    },
    [makeAuthenticatedRequest, notifications, fetchNotifications]
  );

  const refresh = useCallback(() => fetchNotifications(), [fetchNotifications]);

  return { notifications, dismissOne, dismissAll, refresh };
}
